import { useEffect, useMemo } from "react"

import { useSelector } from "react-redux"

import { teeupUserStatusKeys } from "@configs/enums"
import { selectUserId } from "@selectors/activeTeeup"
import { selectTeeupPeople } from "@selectors/teeups"
import { selectUserTimezone } from "@selectors/user"
import Collapse from "@ui/Collapse"
import { UNKNOWN_TIMEZONE } from "@utils/peopleUtils"
import { useTeeupPeopleStore } from "stores/teeupPeopleStore"

import Content from "./Content"
import TimeZoneSummary from "./Modals/TimeZoneSummary"
import SubTitleContent from "./SubTitleContent"
import TitleContent from "./TitleContent"

const attendanceStatuses = new Set([
  teeupUserStatusKeys.going,
  teeupUserStatusKeys.onmyway,
  teeupUserStatusKeys.runningLate,
  teeupUserStatusKeys.arrived,
])

const TeeupPeople = ({ onClick }) => {
  const isCollapsed = useTeeupPeopleStore(state => state.isCollapsed)

  const clearStore = useTeeupPeopleStore(state => state.clear)
  const people = Object.values(useSelector(selectTeeupPeople))
  const userId = useSelector(selectUserId)
  const timezone = useSelector(selectUserTimezone)

  const currentUser = people.find(item => item.id === userId)

  const { peopleAttending, isMultiTimezone, isOrganizer } = useMemo(() => {
    const peopleAttending = people.reduce((accumulatedCount, item) => {
      return attendanceStatuses.has(item.status)
        ? accumulatedCount + 1
        : accumulatedCount
    }, 0)

    // Spec (Multi-TZ Sync Up Detail Group, Notion e8f3d605c41448f98d39588e55f18c8e /
    // "people card requirements (for Alessandra)", ecefe436ffc041b994b32ebf2b3273e1): multi-TZ
    // = 2+ DISTINCT KNOWN participant timezones. Participants with no shared timezone (the
    // "Unknown" bucket) must not by themselves flip a same-TZ TeeUp into a multi-TZ one.
    const knownTimezones = people
      ?.map(item => item.timezone)
      .filter(timezone => timezone && timezone !== UNKNOWN_TIMEZONE)
    const isMultiTimezone = new Set(knownTimezones).size >= 2

    const isOrganizer = people.some(
      item => item.isOrganiser && item.id === userId
    )

    return { peopleAttending, isMultiTimezone, isOrganizer }
  }, [people, timezone])

  useEffect(() => {
    return () => {
      clearStore()
    }
  }, [])

  const handlePeopleClick = () => {
    onClick && onClick()
  }

  return (
    <Collapse
      isParent
      isCollapsed={isCollapsed}
      setIsCollapsed={handlePeopleClick}
      // FIX 2026-07-03 (fidelity pass, People-row truncation root cause): Ale's `.people-card`
      // (coowe.html) has NO "PEOPLE" label inline in the card row -- the section label sits
      // ABOVE the card (her `.section-h`, same pattern as Game plan/Agenda/Tasks). The inline
      // `<h4>People</h4>` Collapse used to render here ate ~55-95px of the row's width, which is
      // why the Organizer label + "N people" text still visually truncated ("Orga...", "1 pe...")
      // even after the ContentWrapper column-width fix. The section label now renders as its own
      // header above <People/> in TeeupPageContent.js; title is empty here so Collapse's shared
      // header layout (still used by the nested Participants panel) is otherwise untouched.
      title=""
      titleContent={
        <TitleContent
          people={people}
          total={people.length}
          attending={peopleAttending}
          isMultiTimezone={isMultiTimezone}
          userId={userId}
          isCollapsed={isCollapsed}
        />
      }
      subTitleContent={
        <SubTitleContent
          people={people}
          isCollapsed={isCollapsed}
          isOrganizer={isOrganizer}
          currentUser={currentUser}
        />
      }>
      <Content people={people} isMultiTimezone={isMultiTimezone} />

      <TimeZoneSummary people={people} />
    </Collapse>
  )
}

export default TeeupPeople
